import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Resolve photo src — photos are base64 data URLs. 🚨 A legacy server-path photo is not shown (Jesse, 2026-10-28):
// the public file mount it pointed at is closed, so it falls back to initials until the photo is re-uploaded.
const resolvePhotoSrc = (photo) =>
  !photo ? null
  : (photo.startsWith('data:') || photo.startsWith('http')) ? photo
  : null;

// ── Shared styles ─────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', height: '40px', padding: '0 12px',
  borderRadius: '8px', border: '1px solid #e5e7eb',
  fontSize: '13px', outline: 'none', boxSizing: 'border-box',
};
const labelStyle = {
  fontSize: '12px', fontWeight: '600', color: '#374151',
  display: 'block', marginBottom: '5px',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const TYPE_STYLES = {
  Owner:  { bg: '#edf1fb', color: '#3a6ad9' },
  Admin:  { bg: '#eff6ff', color: '#3a6ad9' },
  User:   { bg: '#f3f4f6', color: '#374151' },
};

function TypeBadge({ type }) {
  const s = TYPE_STYLES[type] || TYPE_STYLES.User;
  return (
    <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 9px', borderRadius: '9999px', backgroundColor: s.bg, color: s.color }}>
      {type || 'User'}
    </span>
  );
}

function StatusDot({ lastSeen }) {
  const online = lastSeen && (Date.now() - new Date(lastSeen).getTime()) < 5 * 60 * 1000;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ width: '7px', height: '7px', borderRadius: '9999px', backgroundColor: online ? '#acf44e' : '#d1d5db', flexShrink: 0 }} />
      <span style={{ fontSize: '12px', color: online ? '#acf44e' : '#9ca3af' }}>{online ? 'Online' : 'Offline'}</span>
    </div>
  );
}

function InitialsAvatar({ firstName, lastName, size = 36 }) {
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  const hue = ((firstName?.charCodeAt(0) || 65) * 7 + (lastName?.charCodeAt(0) || 65) * 13) % 360;
  return (
    <div style={{
      width: size, height: size, borderRadius: '9999px',
      backgroundColor: `hsl(${hue}, 60%, 88%)`,
      color: `hsl(${hue}, 55%, 32%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: '700', flexShrink: 0,
    }}>{initials}</div>
  );
}

function MemberAvatar({ member, size = 36 }) {
  const [imgError, setImgError] = useState(false);
  if (member.profile_photo && !imgError) {
    return (
      <img
        src={resolvePhotoSrc(member.profile_photo)}
        alt={`${member.first_name} ${member.last_name}`}
        style={{ width: size, height: size, borderRadius: '9999px', objectFit: 'cover', flexShrink: 0 }}
        onError={() => setImgError(true)}
      />
    );
  }
  return <InitialsAvatar firstName={member.first_name} lastName={member.last_name} size={size} />;
}

function relativeTime(iso) {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div style={{
      position: 'fixed', bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))', right: '24px', zIndex: 3000,
      backgroundColor: type === 'error' ? '#ff082f' : '#111827',
      color: '#fff', padding: '12px 20px', borderRadius: '10px',
      fontSize: '13px', fontWeight: '500', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      display: 'flex', alignItems: 'center', gap: '10px', maxWidth: '340px',
    }}>
      <span>{type === 'error' ? '✕' : '✓'}</span>
      {message}
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '400px', padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '10px' }}>{title}</h3>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', lineHeight: 1.6 }}>{message}</p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', backgroundColor: danger ? '#ff082f' : '#3a6ad9', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── Three-dot row menu (fixed-position dropdown to escape overflow:hidden) ────
function RowMenu({ member, currentUserId, currentUserType, onEdit, onResetPassword, onRemove }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });
  const ref = useRef(null);
  const isOwner = member.user_type === 'Owner';
  const isSelf = member.id === currentUserId;
  const currentIsOwner = currentUserType === 'Owner';
  const canEdit = isSelf || currentIsOwner;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const scrollHandler = () => setOpen(false);
    document.addEventListener('mousedown', handler);
    document.addEventListener('scroll', scrollHandler, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('scroll', scrollHandler, true);
    };
  }, []);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open) {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen((v) => !v);
  };

  return (
    <div ref={ref} style={{ display: 'inline-block' }}>
      <button
        onClick={handleToggle}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', color: '#9ca3af', fontSize: '16px', lineHeight: 1 }}
      >•••</button>
      {open && (
        <div style={{
          position: 'fixed', top: dropPos.top, right: dropPos.right,
          backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
          zIndex: 9999, minWidth: '168px',
        }}>
          {canEdit && (
            <button
              onClick={() => { setOpen(false); onEdit(member); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#374151' }}
            >Edit</button>
          )}
          <button
            onClick={() => { setOpen(false); onResetPassword(member); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#374151' }}
          >Reset Password</button>
          {!isOwner && !isSelf && (
            <button
              onClick={() => { setOpen(false); onRemove(member); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#ff082f' }}
            >Remove Member</button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Edit Drawer ───────────────────────────────────────────────────────────────
export function EditDrawer({ member, currentUserType, onClose, onSave, onError }) {
  const token = localStorage.getItem('doorly_admin_token');
  const isOwner = currentUserType === 'Owner';

  const [form, setForm] = useState({
    firstName: member.first_name || '',
    lastName: member.last_name || '',
    email: member.email || '',
    userType: member.user_type || 'Admin',
    jobTitle: member.job_title || '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(
    resolvePhotoSrc(member.profile_photo)
  );
  const [dragging, setDragging] = useState(false);
  const [photoHover, setPhotoHover] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handlePhotoChange = (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image.');
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    if (!form.email.trim()) {
      setError('Email address is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('firstName', form.firstName.trim());
      fd.append('lastName', form.lastName.trim());
      fd.append('email', form.email.trim());
      fd.append('jobTitle', form.jobTitle.trim());
      if (isOwner) fd.append('userType', form.userType);
      if (photoFile) fd.append('photo', photoFile);

      const res = await axios.put(`${API}/api/admin/team/update/${member.id}`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSave(res.data.member);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes.');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2500 }}>
      {/* Backdrop */}
      <div
        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)' }}
        onClick={onClose}
      />
      {/* Drawer */}
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: '420px',
        backgroundColor: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.14)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h3 style={{ fontWeight: '700', fontSize: '16px', color: '#111827', margin: 0 }}>Edit Team Member</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '24px', lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {/* Profile photo */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '28px' }}>
            <div
              onClick={() => fileInputRef.current?.click()}
              onMouseEnter={() => setPhotoHover(true)}
              onMouseLeave={() => setPhotoHover(false)}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); handlePhotoChange(e.dataTransfer.files[0]); }}
              style={{
                width: '80px', height: '80px', borderRadius: '50%',
                cursor: 'pointer', position: 'relative', overflow: 'hidden',
                border: dragging ? '3px dashed #3a6ad9' : '2px solid #e5e7eb',
                flexShrink: 0,
              }}
            >
              {photoPreview ? (
                <img src={photoPreview} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <InitialsAvatar firstName={form.firstName} lastName={form.lastName} size={80} />
              )}
              {/* Hover overlay */}
              {(photoHover || dragging) && (
                <div style={{
                  position: 'absolute', inset: 0,
                  backgroundColor: 'rgba(0,0,0,0.45)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => handlePhotoChange(e.target.files[0])}
            />
            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px', textAlign: 'center' }}>
              Click or drag to upload · JPG, PNG, WebP · Max 5MB
            </p>
          </div>

          {/* Error */}
          {error && (
            <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', backgroundColor: '#fff0f3', border: '1px solid #ffb3bf', fontSize: '13px', color: '#ff082f' }}>
              {error}
            </div>
          )}

          {/* Name */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>First Name</label>
              <input
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Last Name</label>
              <input
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Email */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Email Address</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              style={inputStyle}
            />
          </div>

          {/* User Type */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>User Type</label>
            {isOwner ? (
              <select
                value={form.userType}
                onChange={(e) => setForm((f) => ({ ...f, userType: e.target.value }))}
                style={{ ...inputStyle, backgroundColor: '#fff' }}
              >
                <option value="Owner">Owner</option>
                <option value="Admin">Admin</option>
                <option value="User">User</option>
              </select>
            ) : (
              <div style={{ ...inputStyle, backgroundColor: '#f9fafb', color: '#6b7280', display: 'flex', alignItems: 'center', cursor: 'default' }}>
                {form.userType}
              </div>
            )}
          </div>

          {/* Job Title */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>
              Job Title{' '}
              <span style={{ fontWeight: '400', color: '#9ca3af' }}>(optional)</span>
            </label>
            <input
              value={form.jobTitle}
              onChange={(e) => setForm((f) => ({ ...f, jobTitle: e.target.value }))}
              placeholder="e.g. Loan Officer"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#374151' }}
          >Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', backgroundColor: '#3a6ad9', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminTeam() {
  const token = localStorage.getItem('doorly_admin_token');
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); } catch { return {}; } })();
  const headers = { Authorization: `Bearer ${token}` };

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentUserType, setCurrentUserType] = useState('Admin');

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', userType: 'Admin' });
  const [inviteSaving, setInviteSaving] = useState(false);

  // Pending invitations
  const [pendingInvitations, setPendingInvitations] = useState([]);

  // Edit drawer
  const [editTarget, setEditTarget] = useState(null);

  // Confirm modals
  const [confirmReset, setConfirmReset] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchTeam = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/team`, { headers });
      const list = res.data || [];
      setMembers(list);
      // Determine current user's type from the list
      const me = list.find((m) => m.id === currentUser.id);
      if (me) setCurrentUserType(me.user_type || 'Admin');
    } catch {
      showToast('Failed to load team members.', 'error');
    }
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInvitations = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/admin/team/invitations`, { headers });
      setPendingInvitations(Array.isArray(res.data) ? res.data : []);
    } catch (_) {}
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchTeam(); fetchInvitations(); }, [fetchTeam, fetchInvitations]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteSaving(true);
    try {
      await axios.post(`${API}/api/admin/team/invite`, inviteForm, { headers });
      showToast(`Invitation sent to ${inviteForm.email}`);
      setShowInvite(false);
      setInviteForm({ name: '', email: '', userType: 'Admin' });
      fetchInvitations();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send invitation.', 'error');
    }
    setInviteSaving(false);
  };

  const handleResetPassword = async () => {
    if (!confirmReset) return;
    try {
      await axios.post(`${API}/api/admin/team/reset-password/${confirmReset.id}`, {}, { headers });
      showToast(`Password reset email sent to ${confirmReset.email}`);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send reset email.', 'error');
    }
    setConfirmReset(null);
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    try {
      await axios.put(`${API}/api/admin/team/remove/${confirmRemove.id}`, {}, { headers });
      showToast(`${confirmRemove.first_name} ${confirmRemove.last_name} removed from team.`);
      setMembers((m) => m.filter((x) => x.id !== confirmRemove.id));
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to remove member.', 'error');
    }
    setConfirmRemove(null);
  };

  const handleEditSave = (updated) => {
    // Update member in list
    setMembers((prev) => prev.map((m) => m.id === updated.id ? { ...m, ...updated } : m));
    // If editing self, sync localStorage so navbar updates
    if (updated.id === currentUser.id) {
      const newUser = {
        ...currentUser,
        email: updated.email,
        firstName: updated.first_name,
        lastName: updated.last_name,
        profile_photo: updated.profile_photo,
      };
      localStorage.setItem('doorly_admin_user', JSON.stringify(newUser));
      window.dispatchEvent(new CustomEvent('doorly:profileUpdated'));
    }
    setEditTarget(null);
    showToast('Profile updated');
  };

  const filtered = members.filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      `${m.first_name} ${m.last_name}`.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1100px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', margin: 0 }}>Team</h1>
              {pendingInvitations.length > 0 && (
                <span style={{ background: '#f5b832', color: '#fff', borderRadius: '12px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' }}>
                  {pendingInvitations.length} pending
                </span>
              )}
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280' }}>Manage Doorly Desk admin access and permissions.</p>
          </div>
          <button
            onClick={() => setShowInvite(true)}
            style={{ padding: '9px 18px', borderRadius: '9px', border: 'none', backgroundColor: '#3a6ad9', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span> Invite Member
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px', position: 'relative', display: 'inline-block' }}>
          <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input
            type="text" placeholder="Search by name or email…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ height: '38px', paddingLeft: '32px', paddingRight: '16px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', width: '280px' }}
          />
        </div>

        {/* Table */}
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #f3f4f6', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                {['Member', 'Email', 'User Type', 'Last Activity', 'Status', ''].map((h) => (
                  <th key={h} style={{ padding: '11px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Loading team…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No team members found.</td></tr>
              )}
              {!loading && filtered.map((member) => {
                const isSelf = member.id === currentUser.id;
                const lastActivity = member.last_seen_at || member.last_login;
                return (
                  <tr key={member.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                    {/* Avatar + Name */}
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <MemberAvatar member={member} size={36} />
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {member.first_name} {member.last_name}
                            {isSelf && <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '400' }}>(You)</span>}
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                            {member.job_title
                              ? member.job_title
                              : `Since ${member.created_at ? new Date(member.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Email */}
                    <td style={{ padding: '14px 20px', fontSize: '13px', color: '#374151' }}>{member.email}</td>
                    {/* Type */}
                    <td style={{ padding: '14px 20px' }}><TypeBadge type={member.user_type || 'Admin'} /></td>
                    {/* Last Activity */}
                    <td style={{ padding: '14px 20px', fontSize: '12px', color: '#6b7280' }}>{relativeTime(lastActivity)}</td>
                    {/* Status */}
                    <td style={{ padding: '14px 20px' }}><StatusDot lastSeen={lastActivity} /></td>
                    {/* Actions */}
                    <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                      <RowMenu
                        member={member}
                        currentUserId={currentUser.id}
                        currentUserType={currentUserType}
                        onEdit={(m) => setEditTarget(m)}
                        onResetPassword={(m) => setConfirmReset(m)}
                        onRemove={(m) => setConfirmRemove(m)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Member count */}
        {!loading && (
          <p style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af' }}>
            {filtered.length} member{filtered.length !== 1 ? 's' : ''}
          </p>
        )}

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <div style={{ marginTop: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>Pending Invitations</h3>
              <span style={{ background: '#fff7ed', color: '#7a5200', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' }}>
                {pendingInvitations.length} pending
              </span>
            </div>
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #f3f4f6', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    {['Name / Email', 'Role', 'Invited By', 'Expires', 'Status', ''].map(h => (
                      <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pendingInvitations.map(inv => {
                    const isExpired = new Date(inv.expires_at) < new Date();
                    return (
                      <tr key={inv.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                        <td style={{ padding: '12px 16px' }}>
                          {inv.name && <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{inv.name}</div>}
                          <div style={{ fontSize: inv.name ? '11px' : '13px', color: inv.name ? '#9ca3af' : '#111827' }}>{inv.email}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: '#eff6ff', color: '#2854c0' }}>{inv.role || 'Admin'}</span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>{inv.invited_by || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '12px', color: isExpired ? '#ff082f' : '#6b7280' }}>
                          {isExpired ? 'Expired' : new Date(inv.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: isExpired ? '#fff0f3' : '#fff7ed', color: isExpired ? '#ff082f' : '#7a5200' }}>
                            {isExpired ? 'Expired' : 'Pending'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={async () => {
                                try {
                                  await axios.patch(`${API}/api/admin/team/invitations/${inv.id}`, { action: 'resend' }, { headers });
                                  showToast(`Invitation resent to ${inv.email}`);
                                  fetchInvitations();
                                } catch (err) { showToast(err.response?.data?.error || 'Failed to resend', 'error'); }
                              }}
                              style={{ padding: '5px 10px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                            >Resend</button>
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Cancel invitation for ${inv.email}?`)) return;
                                try {
                                  await axios.patch(`${API}/api/admin/team/invitations/${inv.id}`, { action: 'cancel' }, { headers });
                                  showToast('Invitation cancelled');
                                  fetchInvitations();
                                } catch (err) { showToast(err.response?.data?.error || 'Failed to cancel', 'error'); }
                              }}
                              style={{ padding: '5px 10px', background: '#fff0f3', color: '#ff082f', border: '1px solid #fecdd3', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                            >Cancel</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Invite Modal ──────────────────────────────────────────────────────── */}
      {showInvite && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontWeight: '700', fontSize: '16px', color: '#111827', margin: 0 }}>Invite Team Member</h3>
              <button onClick={() => setShowInvite(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '22px', lineHeight: 1 }}>×</button>
            </div>
            <form onSubmit={handleInvite} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '5px' }}>Full Name *</label>
                <input
                  required value={inviteForm.name}
                  onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Jane Smith"
                  style={{ width: '100%', height: '40px', padding: '0 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '5px' }}>Email Address *</label>
                <input
                  type="email" required value={inviteForm.email}
                  onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="jane@example.com"
                  style={{ width: '100%', height: '40px', padding: '0 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '5px' }}>User Type</label>
                <select
                  value={inviteForm.userType}
                  onChange={(e) => setInviteForm((f) => ({ ...f, userType: e.target.value }))}
                  style={{ width: '100%', height: '40px', padding: '0 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', backgroundColor: '#fff' }}
                >
                  <option value="Admin">Admin</option>
                  <option value="User">User</option>
                </select>
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Owner role cannot be assigned via invite.</p>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '4px' }}>
                <button type="button" onClick={() => setShowInvite(false)} style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Cancel</button>
                <button type="submit" disabled={inviteSaving} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', backgroundColor: '#3a6ad9', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', opacity: inviteSaving ? 0.7 : 1 }}>
                  {inviteSaving ? 'Sending…' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Drawer ───────────────────────────────────────────────────────── */}
      {editTarget && (
        <EditDrawer
          member={editTarget}
          currentUserType={currentUserType}
          onClose={() => setEditTarget(null)}
          onSave={handleEditSave}
        />
      )}

      {/* ── Reset Password Confirm ────────────────────────────────────────────── */}
      {confirmReset && (
        <ConfirmModal
          title="Send Password Reset Email"
          message={`Send a password reset email to ${confirmReset.email}?`}
          confirmLabel="Send Email"
          onConfirm={handleResetPassword}
          onCancel={() => setConfirmReset(null)}
        />
      )}

      {/* ── Remove Member Confirm ─────────────────────────────────────────────── */}
      {confirmRemove && (
        <ConfirmModal
          title="Remove Team Member"
          message={`Remove ${confirmRemove.first_name} ${confirmRemove.last_name} from Doorly Desk? They will lose access immediately.`}
          confirmLabel="Remove"
          danger
          onConfirm={handleRemove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}

      {/* ── Toast ─────────────────────────────────────────────────────────────── */}
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </AdminLayout>
  );
}
